import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Plus, Award, TrendingUp } from "lucide-react";

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monthRange = () => {
  const now = new Date();
  return [iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(new Date(now.getFullYear(), now.getMonth() + 1, 0))];
};
const fmtDate = (d) =>
  d ? new Date(d + (d.length === 10 ? "T00:00:00" : "")).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";

function AmountBadge({ amount }) {
  const positive = amount >= 0;
  return (
    <span className={`font-medium tabular-nums ${positive ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
      {positive ? "+" : ""}
      {amount}
    </span>
  );
}

// ----------------------------- Adjustments tab -----------------------------
function AdjustmentsTab({ students }) {
  const [studentId, setStudentId] = useState("");
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/students/${id}/points-history`);
      setHistory(data);
    } catch (e) {
      toast.error("Failed to load points history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (studentId) load(studentId);
    else setHistory(null);
  }, [studentId, load]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseInt(amount, 10);
    if (!Number.isInteger(amt) || amt === 0) {
      toast.error("Enter a non-zero whole number (use a minus sign for deductions)");
      return;
    }
    if (!note.trim()) {
      toast.error("A note is required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/students/${studentId}/point-adjustments`, { amount: amt, note: note.trim() });
      toast.success("Adjustment added");
      setAmount("");
      setNote("");
      load(studentId);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2 max-w-sm">
        <Label>Student</Label>
        <Select value={studentId} onValueChange={setStudentId}>
          <SelectTrigger className="h-11" data-testid="adjust-student-select">
            <SelectValue placeholder="Select a student…" />
          </SelectTrigger>
          <SelectContent>
            {students.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.full_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!studentId ? (
        <div className="bg-card border border-border rounded-lg p-10 text-center text-muted-foreground">
          Select a student to view their points history and add adjustments.
        </div>
      ) : loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : history ? (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Record points</p>
              <p className="font-heading text-2xl font-medium" data-testid="hist-record-points">{history.record_points}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Adjustments</p>
              <p className="font-heading text-2xl font-medium" data-testid="hist-adjustment-points">{history.adjustment_points}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Total</p>
              <p className="font-heading text-2xl font-medium text-primary" data-testid="hist-total">{history.total}</p>
            </div>
          </div>

          {/* Add adjustment */}
          <form onSubmit={submit} className="bg-card border border-border rounded-lg p-4 space-y-3" data-testid="adjustment-form">
            <p className="text-sm font-medium">Add a point adjustment</p>
            <div className="flex flex-wrap gap-3 items-end">
              <div className="space-y-1.5">
                <Label className="text-xs">Amount (use − for deductions)</Label>
                <Input
                  type="number"
                  className="h-11 w-40"
                  placeholder="e.g. -50"
                  data-testid="adjustment-amount-input"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <div className="space-y-1.5 flex-1 min-w-[200px]">
                <Label className="text-xs">Note (required)</Label>
                <Input
                  className="h-11"
                  placeholder="Reason for adjustment"
                  data-testid="adjustment-note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={saving} className="h-11" data-testid="adjustment-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1.5" /> Add</>}
              </Button>
            </div>
          </form>

          {/* History line items */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <p className="text-sm font-medium">Points history</p>
            </div>
            {history.items.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground" data-testid="history-empty">No points activity yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Note</TableHead>
                      <TableHead>By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.items.map((it, idx) => (
                      <TableRow key={idx} data-testid={`history-row-${it.kind}-${idx}`}>
                        <TableCell className="text-muted-foreground whitespace-nowrap">{fmtDate(it.date)}</TableCell>
                        <TableCell>
                          {it.kind === "adjustment" ? (
                            <Badge className="bg-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))] text-[#1C2321]">Adjustment</Badge>
                          ) : (
                            <Badge variant="secondary">Daily record</Badge>
                          )}
                        </TableCell>
                        <TableCell><AmountBadge amount={it.amount} /></TableCell>
                        <TableCell className="text-muted-foreground max-w-xs truncate" title={it.note}>{it.note || "—"}</TableCell>
                        <TableCell className="text-muted-foreground">{it.kind === "adjustment" ? it.admin_name : "Teacher"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

// ------------------------------- Reports tab -------------------------------
function ReportsTab({ students }) {
  const [m] = useState(monthRange);
  const [scope, setScope] = useState("school");
  const [start, setStart] = useState(m[0]);
  const [end, setEnd] = useState(m[1]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start, end };
      if (scope !== "school") params.student_id = scope;
      const { data } = await api.get("/admin/points-report", { params });
      setReport(data);
    } catch (e) {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [scope, start, end]);

  useEffect(() => {
    const t = setTimeout(run, 300);
    return () => clearTimeout(t);
  }, [run]);

  const setThisMonth = () => {
    const [s, e] = monthRange();
    setStart(s);
    setEnd(e);
  };

  return (
    <div className="space-y-5">
      <div className="bg-card border border-border rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Scope</Label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="h-11 w-52" data-testid="report-scope-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="school">School-wide</SelectItem>
              {students.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">From</Label>
          <Input type="date" className="h-11" data-testid="report-start" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">To</Label>
          <Input type="date" className="h-11" data-testid="report-end" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
        <Button variant="outline" className="h-11" onClick={setThisMonth} data-testid="report-this-month">This month</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : report ? (
        <>
          <div className="grid grid-cols-3 gap-3" data-testid="report-summary">
            <div className="bg-card border border-border rounded-lg p-5">
              <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Record points</p>
              <p className="font-heading text-3xl font-medium">{report.record_points}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-5">
              <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Adjustments</p>
              <p className="font-heading text-3xl font-medium">{report.adjustment_points}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-5">
              <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Total</p>
              <p className="font-heading text-3xl font-medium text-primary" data-testid="report-total">{report.total}</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {report.scope === "student" ? report.student_name : "School-wide"} · {fmtDate(report.start)} – {fmtDate(report.end)}
          </p>

          {report.scope === "school" && (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              {report.students.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground" data-testid="report-empty">No points in this range.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead>Record points</TableHead>
                        <TableHead>Adjustments</TableHead>
                        <TableHead>Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.students.map((s) => (
                        <TableRow key={s.student_id} data-testid={`report-row-${s.student_id}`}>
                          <TableCell className="font-medium">{s.student_name}</TableCell>
                          <TableCell>{s.record_points}</TableCell>
                          <TableCell><AmountBadge amount={s.adjustment_points} /></TableCell>
                          <TableCell className="font-medium">{s.total}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

export default function Points() {
  const [students, setStudents] = useState([]);

  useEffect(() => {
    api.get("/admin/students").then((r) => setStudents(r.data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-5xl" data-testid="points-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-foreground">Points</h1>
      <p className="mt-1 text-muted-foreground">Adjust points and run points reports.</p>

      <Tabs defaultValue="adjustments" className="mt-6">
        <TabsList>
          <TabsTrigger value="adjustments" data-testid="tab-adjustments">
            <Award className="h-4 w-4 mr-2" /> Adjustments
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">
            <TrendingUp className="h-4 w-4 mr-2" /> Reports
          </TabsTrigger>
        </TabsList>
        <TabsContent value="adjustments" className="mt-5">
          <AdjustmentsTab students={students} />
        </TabsContent>
        <TabsContent value="reports" className="mt-5">
          <ReportsTab students={students} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
